import React, { useLayoutEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

interface TruncateTooltipProps {
    text: string;
    className?: string;
}

export const TruncateTooltip: React.FC<TruncateTooltipProps> = ({
    text,
    className = "",
}) => {
    const spanRef = useRef<HTMLSpanElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncate = () => {
        const span = spanRef.current;
        const container = containerRef.current;
        if (!span || !container) return;
        setIsTruncated(span.scrollWidth > container.clientWidth + 1);
    };

    useLayoutEffect(() => {
        checkTruncate();
        const span = spanRef.current;
        const container = containerRef.current;

        let ro: ResizeObserver | null = null;
        try {
            ro = new ResizeObserver(() => {
                checkTruncate();
            });
            if (span) ro.observe(span);
            if (container) ro.observe(container);
        } catch {
            window.addEventListener("resize", checkTruncate);
        }

        return () => {
            if (ro) {
                ro.disconnect();
            } else {
                window.removeEventListener("resize", checkTruncate);
            }
        };
    }, [text]);

    const content = (
        <div ref={containerRef} className="w-full">
            <span
                ref={spanRef}
                className={`inline-block truncate cursor-default ${className}`}
                style={{ maxWidth: "100%" }}
            >
                {text}
            </span>
        </div>
    );

    if (!isTruncated) return content;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent>
                <p className="max-w-sm break-words">{text}</p>
            </TooltipContent>
        </Tooltip>
    );
};
